import React from 'react';
import { formatDate } from 'utils/date.util';

import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  VStack,
  HStack,
  Link,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';

import { useColors } from './colors';
import { fulfillmentStatusBadge, formatMoney } from '../../utils/orderStatus.util';

const EbayLineItems = ({ lineItems }) => {
  const { borderColor, headerBg, textColor, subTextColor, cardBg } = useColors();

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" overflowX="auto" bg={cardBg}>
      <Table size="sm" variant="simple">
        <Thead bg={headerBg}>
          <Tr>
            <Th>Item</Th>
            <Th>SKU / Item ID</Th>
            <Th isNumeric>Qty</Th>
            <Th isNumeric>Unit</Th>
            <Th isNumeric>Line total</Th>
            <Th isNumeric>Shipping</Th>
            <Th isNumeric>Tax</Th>
            <Th>Fulfillment</Th>
          </Tr>
        </Thead>
        <Tbody>
          {lineItems.map((item) => {
            const status = fulfillmentStatusBadge(item.lineItemFulfillmentStatus);
            const taxTotal =
              item.taxes?.reduce((sum, t) => sum + Number(t.amount?.value || 0), 0) || 0;
            return (
              <Tr key={item.lineItemId}>
                <Td maxW="280px">
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium" color={textColor} noOfLines={2}>
                      {item.title}
                    </Text>
                    <HStack spacing={1}>
                      <Badge variant="outline" fontSize="2xs">
                        {item.soldFormat || 'FIXED_PRICE'}
                      </Badge>
                      {item.lineItemFulfillmentInstructions?.guaranteedDelivery && (
                        <Badge colorScheme="green" fontSize="2xs">
                          Guaranteed delivery
                        </Badge>
                      )}
                    </HStack>
                  </VStack>
                </Td>
                <Td>
                  <Text fontSize="xs" fontFamily="mono" color={textColor}>
                    {item.sku}
                  </Text>
                  <Link
                    href={`https://www.ebay.com/itm/${item.legacyItemId}`}
                    isExternal
                    fontSize="2xs"
                    color="blue.500"
                  >
                    {item.legacyItemId} <ExternalLinkIcon mx="2px" />
                  </Link>
                </Td>
                <Td isNumeric>{item.quantity}</Td>
                <Td isNumeric>
                  {formatMoney(
                    Number(item.lineItemCost?.value) / (item.quantity || 1),
                    item.lineItemCost?.currency,
                  )}
                </Td>
                <Td isNumeric fontWeight="semibold">
                  {formatMoney(item.total?.value || item.lineItemCost?.value, item.total?.currency)}
                </Td>
                <Td isNumeric>
                  {formatMoney(
                    item.deliveryCost?.shippingCost?.value,
                    item.deliveryCost?.shippingCost?.currency,
                  )}
                </Td>
                <Td isNumeric>{formatMoney(taxTotal)}</Td>
                <Td>
                  <Badge colorScheme={status.colorScheme} variant="subtle" fontSize="xs">
                    {status.label}
                  </Badge>
                  {item.lineItemFulfillmentInstructions?.shipByDate && (
                    <Text fontSize="2xs" color={subTextColor} mt={0.5}>
                      Ship by{' '}
                      {formatDate(item.lineItemFulfillmentInstructions.shipByDate)}
                    </Text>
                  )}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

const AmazonLineItems = ({ lineItems }) => {
  const { borderColor, headerBg, textColor, cardBg } = useColors();

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" overflowX="auto" bg={cardBg}>
      <Table size="sm" variant="simple">
        <Thead bg={headerBg}>
          <Tr>
            <Th>Item</Th>
            <Th>ASIN / SKU</Th>
            <Th isNumeric>Qty</Th>
            <Th isNumeric>Unit price</Th>
            <Th isNumeric>Line total</Th>
            <Th>Fulfillment</Th>
          </Tr>
        </Thead>
        <Tbody>
          {lineItems.map((item) => {
            const status = fulfillmentStatusBadge(item.lineItemFulfillmentStatus);
            return (
              <Tr key={item.lineItemId}>
                <Td maxW="280px">
                  <Text fontSize="sm" fontWeight="medium" color={textColor} noOfLines={2}>
                    {item.title}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="xs" fontFamily="mono">
                    {item.sku}
                  </Text>
                  {item.asin && (
                    <Link
                      href={`https://www.amazon.com/dp/${item.asin}`}
                      isExternal
                      fontSize="2xs"
                      color="blue.500"
                    >
                      {item.asin} <ExternalLinkIcon mx="2px" />
                    </Link>
                  )}
                </Td>
                <Td isNumeric>{item.quantity}</Td>
                <Td isNumeric fontWeight="semibold">
                  {formatMoney(item.lineItemCost?.value, item.lineItemCost?.currency)}
                </Td>
                <Td isNumeric fontWeight="semibold">
                  {formatMoney(item.total?.value, item.total?.currency)}
                </Td>
                <Td>
                  <Badge colorScheme={status.colorScheme} variant="subtle" fontSize="xs">
                    {status.label}
                  </Badge>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

const LineItemsTab = ({ order }) => {
  const { subTextColor } = useColors();
  const isEbay = order.marketplace === 'ebay';
  const items = order.lineItems || [];

  if (!items.length) {
    return (
      <Text color={subTextColor} fontSize="sm">
        No line items for this order.
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={3}>
      <Text fontSize="sm" color={subTextColor}>
        {isEbay
          ? 'eBay line items — SKU, item ID, costs, taxes, and fulfillment per line.'
          : 'Amazon line items — ASIN, seller SKU, quantities, and fulfillment per line.'}
      </Text>
      {isEbay ? <EbayLineItems lineItems={items} /> : <AmazonLineItems lineItems={items} />}
    </VStack>
  );
};

export default LineItemsTab;
